/* FeestXL collection navigation, Shopify facets and explicit party selection. */
(() => {
  'use strict';
  if (customElements.get('fxl-collection-v2')) return;
  class Collection extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.strings = JSON.parse(this.querySelector('[data-fxlc-strings]').textContent);
      this.key = this.dataset.collection;
      this.mobile = matchMedia('(max-width: 767px)');
      this.locationKey = location.pathname + location.search;
      this.money = new Intl.NumberFormat(this.dataset.locale || 'nl', { style: 'currency', currency: this.dataset.currency || 'EUR' });
      this.on(this, 'click', e => this.click(e));
      this.on(this, 'change', e => this.change(e));
      this.on(this, 'submit', e => { if (e.target.matches('[data-fxlc-facets]')) { e.preventDefault(); if (this.validFacets()) this.update(this.facetURL()); } });
      this.on(window, 'popstate', () => { const key = location.pathname + location.search; if (key !== this.locationKey) this.update(new URL(location.href), false); });
      this.on(window, 'resize', () => this.navArrows());
      this.on(this.mobile, 'change', () => this.filterMode());
      this.querySelectorAll('dialog').forEach(dialog => {
        this.on(dialog, 'close', () => this.unlock());
      });
      ['[data-fxlc-filter-open]', '[data-fxlc-filter-close]'].forEach(s => { const el = this.querySelector(s); if (el) el.hidden = false; });
      this.filterMode();
      this.initNavigation();
      const count = Number(this.querySelector('[data-fxlc-active]')?.dataset.fxlcActive || 0);
      const badge = this.querySelector('[data-fxlc-filter-count]');
      if (badge) badge.textContent = count ? `(${count})` : '';
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.request?.abort(); this.previewRequest?.abort();
      clearTimeout(this.previewTimer); this.observer?.disconnect(); this.unlock();
    }
    on(target, type, fn) { target.addEventListener(type, fn, { signal: this.controller.signal }); }
    t(key, count) { return (this.strings[key] || key).replaceAll('[count]', String(count ?? '')); }
    status(message) { this.querySelector('[data-fxlc-status]').textContent = message; }
    lock() { if (this.previousOverflow === undefined) { this.previousOverflow = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden'; } }
    unlock() { if (this.previousOverflow !== undefined) { document.documentElement.style.overflow = this.previousOverflow; delete this.previousOverflow; } }
    openDialog(dialog) { if (!dialog) return; if (dialog.open) dialog.close(); dialog.showModal(); this.lock(); }
    filterMode() {
      const dialog = this.querySelector('[data-fxlc-filters]');
      if (!dialog) return;
      if (dialog.open) dialog.close();
      if (!this.mobile.matches) dialog.setAttribute('open', '');
    }
    click(e) {
      const el = e.target.closest('button,a');
      if (!el || !this.contains(el)) return;
      if (el.matches('[data-fxlc-update], .fxlc-pagination a')) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault(); this.update(new URL(el.href));
      } else if (el.matches('[data-fxlc-jump-link]')) {
        e.preventDefault(); this.jump(el.hash);
      } else if (el.matches('[data-fxlc-filter-open]')) this.openDialog(this.querySelector('[data-fxlc-filters]'));
      else if (el.matches('[data-fxlc-filter-close]')) this.querySelector('[data-fxlc-filters]').close();
      else if (el.matches('[data-fxlc-retry]')) this.update(this.lastURL);
      else if (el.matches('[data-fxlc-nav-prev],[data-fxlc-nav-next]')) {
        const nav = this.querySelector('[data-fxlc-nav]'); nav.scrollBy({ left: nav.clientWidth * (el.hasAttribute('data-fxlc-nav-prev') ? -.8 : .8), behavior: this.motion() });
      }
    }
    change(e) {
      const target = e.target;
      if (target.matches('[data-fxlc-jump]')) { if (target.value) this.jump(target.value); return; }
      if (target.matches('[data-fxlc-sort]') || target.closest('[data-fxlc-facets]')) {
        if (!this.validFacets()) return;
        if (this.mobile.matches && target.closest('[data-fxlc-facets]')) {
          clearTimeout(this.previewTimer); this.previewRequest?.abort();
          this.previewTimer = setTimeout(() => this.previewFacets(), 160);
        } else this.update(this.facetURL());
        return;
      }
    }
    validFacets() {
      const form = this.querySelector('[data-fxlc-facets]');
      const range = form?.querySelector('.fxlc-price-range');
      if (range) { const inputs = range.querySelectorAll('input'); inputs[1].min = inputs[0].value || '0'; }
      return !form || form.reportValidity();
    }
    facetURL() {
      const url = new URL(location.href); url.hash = '';
      [...url.searchParams.keys()].forEach(key => { if (key.startsWith('filter.') || key === 'sort_by' || key === 'page' || key === 'section_id') url.searchParams.delete(key); });
      for (const [key, value] of new FormData(this.querySelector('[data-fxlc-facets]'))) if (String(value).trim()) url.searchParams.append(key, value);
      return url;
    }
    async fetchSection(url, signal) {
      const endpoint = new URL(url); endpoint.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(endpoint, { signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Section unavailable');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = parsed.querySelector('fxl-collection-v2');
      if (!fresh || fresh.dataset.sectionId !== this.dataset.sectionId) throw new Error('Invalid section');
      return fresh;
    }
    async update(url, push = true) {
      this.lastURL = url; this.request?.abort(); this.previewRequest?.abort(); clearTimeout(this.previewTimer);
      const request = this.request = new AbortController();
      this.setAttribute('aria-busy', 'true'); this.status(this.t('loading')); this.querySelector('[data-fxlc-retry]').hidden = true;
      try {
        const fresh = await this.fetchSection(url, request.signal);
        if (request !== this.request || request.signal.aborted) return;
        this.querySelectorAll('dialog[open]').forEach(d => d.close());
        if (push && url.href !== location.href) history.pushState({}, '', url);
        this.replaceWith(fresh);
        fresh.status(fresh.t('updated', fresh.dataset.total));
        const toolbar = fresh.querySelector('[data-fxlc-toolbar]');
        toolbar.scrollIntoView({ block: 'start' });
        const results = fresh.querySelector('[data-fxlc-results]'); results.focus({ preventScroll: true });
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.removeAttribute('aria-busy'); this.status(this.t('load_error')); this.querySelector('[data-fxlc-retry]').hidden = false;
      }
    }
    async previewFacets() {
      const request = this.previewRequest = new AbortController();
      const apply = this.querySelector('[data-fxlc-apply]'); apply.textContent = this.t('loading');
      try {
        const fresh = await this.fetchSection(this.facetURL(), request.signal);
        if (request !== this.previewRequest || request.signal.aborted) return;
        apply.textContent = this.t('apply', fresh.dataset.total);
        const inputs = [...fresh.querySelectorAll('[data-fxlc-facets] input[type=checkbox]')];
        this.querySelectorAll('[data-fxlc-facets] input[type=checkbox]').forEach(input => {
          const next = inputs.find(other => other.name === input.name && other.value === input.value);
          if (next) { input.disabled = !input.checked && next.disabled; input.closest('label').querySelector('[data-fxlc-value-count]').textContent = next.closest('label').querySelector('[data-fxlc-value-count]').textContent; }
        });
      } catch (error) { if (error.name !== 'AbortError') apply.textContent = this.t('apply', '…'); }
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    target(hash) { let id; try { id = decodeURIComponent(hash.slice(1)); } catch { return null; } const el = document.getElementById(id); return el && this.contains(el) ? el.closest('.product-series-block') : null; }
    jump(hash) { const target = this.target(hash); if (!target) return; target.scrollIntoView({ behavior: this.motion(), block: 'start' }); target.tabIndex = -1; target.focus({ preventScroll: true }); history.replaceState(history.state, '', hash); }
    initNavigation() {
      const jump = this.querySelector('[data-fxlc-jump]');
      this.links = [];
      this.querySelectorAll('[data-fxlc-jump-link]').forEach(link => {
        const block = this.target(link.hash); const ids = new Set([...block?.querySelectorAll('.fxl-product-card[data-product-id]') || []].map(card => card.dataset.productId));
        if (!block || !ids.size) { link.hidden = true; return; }
        const label = link.querySelector('.fxl-filter-pill-text') || link;
        const title = label.textContent.trim().replace(/\s+/g, ' ');
        const count = document.createElement('span'); count.className = 'fxlc-nav-count'; count.textContent = String(ids.size); link.append(count);
        this.links.push({ link, block, title });
        if (jump) jump.add(new Option(title, link.hash));
      });
      if (jump && this.links.length) jump.closest('[data-fxlc-jump-wrap]').hidden = false;
      const explore = this.querySelector('.fxlc-explore'); if (explore && !this.links.length) explore.hidden = true;
      this.querySelectorAll('.product-series-block').forEach(block => {
        const count = new Set([...block.querySelectorAll('.fxl-product-card[data-product-id]')].map(card => card.dataset.productId)).size;
        const label = block.querySelector('.series-title__count'); if (label) label.textContent = `(${this.t('products', count)})`;
        if (block.hasAttribute('data-fxlc-series') && document.querySelector('[data-fxlf-config]')) {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'fxlc-series-build'; button.dataset.fxlfSeries = ''; button.textContent = this.t('save_series'); block.querySelector('.series-block__heading')?.append(button);
        }
      });
      this.observer = new IntersectionObserver(entries => {
        const entry = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (!entry) return;
        this.links.forEach(({ link, block }) => { const active = block === entry.target; link.classList.toggle('is-active', active); if (active) { link.setAttribute('aria-current', 'location'); if (jump) jump.value = link.hash; } else link.removeAttribute('aria-current'); });
      }, { rootMargin: '-170px 0px -55% 0px' });
      this.links.forEach(({ block }) => this.observer.observe(block));
      const nav = this.querySelector('[data-fxlc-nav]'); if (nav) this.on(nav, 'scroll', () => this.navArrows());
      requestAnimationFrame(() => { if (this.isConnected) this.navArrows(); });
    }
    navArrows() {
      const nav = this.querySelector('[data-fxlc-nav]'); if (!nav) return;
      const prev = this.querySelector('[data-fxlc-nav-prev]'), next = this.querySelector('[data-fxlc-nav-next]');
      const overflow = nav.scrollWidth > nav.clientWidth + 2;
      if (prev) { prev.hidden = !overflow; prev.disabled = nav.scrollLeft <= 1; }
      if (next) { next.hidden = !overflow; next.disabled = nav.scrollLeft + nav.clientWidth >= nav.scrollWidth - 2; }
    }
  }
  customElements.define('fxl-collection-v2', Collection);
})();
